<template>
  <div>
    <transition name="transition-fade">
      <ProgressLoader v-if="showProgressLoader"
                      :percentageCompleted="displayPercentage"
                      @hideProgressLoader="showProgressLoader = false"></ProgressLoader>
    </transition>
    <div v-show="!showProgressLoader"
         :class="classList"
         data-upload-wrapper
         @click="handleClick">
      <input
          id="upload"
          ref="upload"
          data-upload
          type="file"
          class="hidden"
          accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.tiff,.tif"
          multiple
          @change="handleFileChange"
      >
      <ActionButton
          v-if="isActionBtn"
          :label="$t('message.imageSelector.uploadFirstImage')"
          ref="emptyUploadBtn"
          class="btn--primary w-full sm:w-auto"
      />
      <IconButton
          v-if="!isActionBtn"
          icon="plus-circle"
          :loading="uploading"
          :label="$t('message.imageSelector.uploadLabel')"
          size="large"
          class="absolute w-full h-full top-0 left-0"
          aria-label="Upload an image"
          ref="uploadBtn"
          @click="handleClick"
      />
    </div>
  </div>
</template>

<script>
import { isValidMimeType, upload, isValidPixelArea } from '@canvaspop/scoops'
import IconButton from '@vuedir/components/IconButton'
import ActionButton from '@vuedir/components/ActionButton'
import ProgressLoader from '@vuedir/components/ProgressLoader'

/**
 * Uploader.vue
 */

export default {
  components: {
    ProgressLoader,
    ActionButton,
    IconButton
  },
  props: {
    uploading: {
      type: Boolean,
      default: false,
    },
    isActionBtn: {
      type: Boolean,
      default: false,
    },
    droppedImages: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      declinedFileNames: [],
      declinedFileSizeFileNames: [],
      uploadPercentages: [],
      totalUploadPercentage: 0,
      showProgressLoader: false,
    }
  },

  emits: ['uploadStart', 'uploadEnd', 'uploadError'],

  watch: {
    uploadPercentages: {
      handler (uploadPercentages) {
        // An array that updates onUploadProgress of each upload
        let totalUploadPercentage

        if (uploadPercentages.length > 0) {
          if (uploadPercentages.length > 1) {
            // If more than 1 upload:
            // 1. Get the sum of all the upload percentages
            totalUploadPercentage = uploadPercentages.reduce(function (a, b) { return a + b.percentage }, 0)
            // 2. Get the summed percentage (i.e. 400 / 4 = 100%)
            totalUploadPercentage = (totalUploadPercentage / uploadPercentages.length)
          } else {
            totalUploadPercentage = uploadPercentages[0].percentage
          }

          // Make the upload percentage 85% of the completion total
          this.totalUploadPercentage = totalUploadPercentage * 0.85
        }

      },
      deep: true,
    },

    droppedImages: {
      handler: function (newImages) {
        this.handleFileUpload(newImages)
      },
      deep: true
    }
  },

  computed: {
    classList () {
      return [
        'cursor-pointer',
        this.isActionBtn ? 'relative' : 'absolute w-full h-full top-0 left-0',
      ]
    },
    displayPercentage () {
      return Math.round(this.totalUploadPercentage)
    }
  },

  methods: {
    handleClick () {
      if (!this.uploading) {
        this.selectFile()
      }
    },

    selectFile () {
      this.$refs.upload.click()
    },

    async handleFileChange (event) {
      const fileList = event.target ? event.target.files : event.dataTransfer.files
      const files = [...fileList]

      this.handleFileUpload(files)
    },
    handleFileUpload (files) {
      this.$emit('uploadStart')
      this.showProgressLoader = true

      this.declinedFileNames = []
      this.declinedFileSizeFileNames = []

      const filesList = [...files]
      this.validateFiles(filesList)
          .then(this.validateImageResolution)
          .then(this.uploadFiles)
          .then(this.emitFiles)
          .catch(this.handleUploadError)
    },
    validateFiles(files) {
      return new Promise(async (resolve, reject) => {
        let acceptedFiles = []

        for (let i = 0; i < files.length; i++) {
          const isValid = await isValidMimeType(files[i])
          .catch((fileName) => {

            const errorMessage = this.$t('message.uploader.mimeErrorMessage', {fileName: fileName})
            this.handleUploadError(errorMessage)
          })

          if (isValid) {
            acceptedFiles.push(files[i])
          } else {
            this.declinedFileNames.push(files[i].name)
          }
        }

        resolve(acceptedFiles)
      })
    },
    async validateImageResolution(files) {
      return new Promise(async (resolve, reject) => {
        let acceptedFiles = []

        for (let i = 0; i < files.length; i++) {

          let isValid = false
          let highEfficiencyImageMimeTypes = ['image/heif', 'image/heic', 'image/heif-sequence', 'image/heic-sequence']
          let matchesHighEfficiencyImageFormat = highEfficiencyImageMimeTypes.filter(item => item.includes(files[i].type));

          if (matchesHighEfficiencyImageFormat.length > 0) {
            isValid = true
          } else {
            isValid = await isValidPixelArea(files[i])
                .catch((errorMessage) => {
                  this.handleUploadError(errorMessage)
                })
          }

          if (isValid) {
            acceptedFiles.push(files[i])
          } else {
            this.declinedFileSizeFileNames.push(files[i].name)
          }
        }

        resolve(acceptedFiles)
      })
    },
    async uploadFiles(acceptedFiles) {
      this.totalUploadPercentage = 0
      this.uploadPercentages = []
      acceptedFiles.forEach((file, index) => {
        this.uploadPercentages.push({
          id: index,
          percentage: 0
        })
      })

      let batches = []
      let that = this
      let uploadBatchSize = 3
      // run uploads in batches of 3
      for await (const batch of batchUploads(acceptedFiles, uploadBatchSize)) {
        batches = batches.concat(batch);
      }

      async function* batchUploads(files, limit) {
        // iterate over files
        for (let i = 0; i < files.length; i = i + limit) {
          // grab the batch of files for current iteration
          const batch = files.slice(i, i + limit);
          let uploads = []
          let exitLoop = false

          // wait for them to resolve concurrently
          for (let t = 0; t < batch.length; t++) {
            let data = new FormData()
            data.append('file', batch[t])
            data.append('persistable', true)
            const config = {
              onUploadProgress: function(progressEvent) {
                // Set the individual load percentages of each image
                const upload = that.uploadPercentages.find(upload => upload.id === t + i)
                upload.percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              }.bind(that)
            }

            try {
              uploads.push(upload(data, config))
            } catch (error) {

              that.handleUploadError(error)
              if (error.response.status === 500) {
                // stop future calls on system error
                exitLoop = true
              }
            }
          }

          if (exitLoop) {
            break
          }

          const result = await Promise.all(uploads);
          // yield the batched result and let consumer know
          yield result;
        }
      }
      return batches
    },
    emitFiles(uploadResponse) {
      const data = uploadResponse.map(file => file.data)
      this.$emit('uploadEnd', data)
      this.totalUploadPercentage += 15
      this.showDeclinedFileNames()
      this.showDeclinedFileSizeFileNames()

      this.$refs.upload.value = ''
    },
    showDeclinedFileNames() {
      if (this.declinedFileNames.length > 0) {
        let declinedFileNames = ''
        this.declinedFileNames.forEach((declinedFileName, i) => {
          if (i > 0) {
            declinedFileNames += ', '
          }
          declinedFileNames += declinedFileName
        })
        const errorMessage = this.$t('message.constants.warnings.warningDeclinedFileNames', {declinedFileNames: declinedFileNames})
        this.handleUploadError(errorMessage + '. ' + this.$t('message.constants.errors.errorCannotUpload'))
      }
    },
    showDeclinedFileSizeFileNames() {
      if (this.declinedFileSizeFileNames.length > 0) {
        let declinedFileSizeFileNames = ''
        this.declinedFileSizeFileNames.forEach((declinedFileName, i) => {
          if (i > 0) {
            declinedFileSizeFileNames += ', '
          }
          declinedFileSizeFileNames += declinedFileName
        })
        const errorMessage = 'Sorry, we could not upload ' + declinedFileSizeFileNames + '. Your image must be no larger than ' + 26000 + 'px x ' + 26000 + 'px and no smaller than ' + 1000 + 'px x ' + 1000 + 'px'
        this.handleUploadError(errorMessage)
      }
    },
    handleUploadError(errorMessage) {
      const errorResponse = {
        data: {
          errorMessage: errorMessage
        }
      }
      this.showProgressLoader = false
      this.$emit('uploadError', errorResponse)
      this.$refs.upload.value = ''
    }
  },
}
</script>
